import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { gridReducer, createInitialGrid } from './gridReducer'
import { loadGrid, saveGrid } from './gridStorage'

const DEFAULT_LIMITS = { maxWindows: 6, maxPerColumn: 3, maxColumns: 3 }

// Owns a tiled window grid: reducer state (lazy-loaded per browser tab),
// debounced persistence, and a stable action API. Feature-agnostic - the
// consumer supplies how each window renders. Reusable per page via `storageKey`.
// Always keeps at least one window so there is an active target for selections.
export default function useWindowGrid({ storageKey, limits, initialType = 'window' } = {}) {
  const cfg = useMemo(() => ({ ...DEFAULT_LIMITS, ...(limits || {}) }), [limits])
  const [state, dispatch] = useReducer(gridReducer, null, () => {
    const loaded = storageKey ? loadGrid(storageKey) : createInitialGrid(initialType)
    const count = loaded.columns.reduce((n, col) => n + col.windows.length, 0)
    return count === 0 ? createInitialGrid(initialType) : loaded
  })

  // Persist (debounced) whenever the layout changes.
  const timerRef = useRef(null)
  useEffect(() => {
    if (!storageKey) return undefined
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => saveGrid(storageKey, state), 150)
    return () => clearTimeout(timerRef.current)
  }, [storageKey, state])

  const addWindow = useCallback(
    ({ type, entityId = null } = {}) =>
      dispatch({ type: 'ADD_WINDOW', entityType: type, entityId, limits: cfg }),
    [cfg]
  )
  // Load an entity into the active window (does not create a new one).
  const selectEntity = useCallback(
    ({ type, entityId }) =>
      dispatch({ type: 'SELECT_ENTITY', entityType: type, entityId, limits: cfg }),
    [cfg]
  )
  const setWindowEntity = useCallback(
    (id, entityId) => dispatch({ type: 'SET_ENTITY', id, entityId }),
    []
  )
  const closeWindow = useCallback((id) => dispatch({ type: 'CLOSE', id }), [])
  const minimizeWindow = useCallback((id) => dispatch({ type: 'MINIMIZE', id }), [])
  const restoreWindow = useCallback((id) => dispatch({ type: 'RESTORE', id }), [])
  const focusWindow = useCallback((id) => dispatch({ type: 'FOCUS', id }), [])
  const setColumnSizes = useCallback((sizes) => dispatch({ type: 'SET_COLUMN_SIZES', sizes }), [])
  const setWindowSizes = useCallback(
    (columnId, sizes) => dispatch({ type: 'SET_WINDOW_SIZES', columnId, sizes }),
    []
  )
  const setPanes = useCallback((id, panes) => dispatch({ type: 'SET_PANES', id, panes }), [])
  const setSolo = useCallback((id) => dispatch({ type: 'SET_SOLO', id }), [])

  const totalWindows = useMemo(
    () => state.columns.reduce((n, col) => n + col.windows.length, 0),
    [state.columns]
  )

  // Columns/windows currently rendered (minimized windows filtered out).
  const visibleColumns = useMemo(
    () =>
      state.columns
        .map((col) => ({ ...col, windows: col.windows.filter((w) => !w.minimized) }))
        .filter((col) => col.windows.length > 0),
    [state.columns]
  )

  const minimized = useMemo(() => {
    const list = []
    state.columns.forEach((col) => col.windows.forEach((w) => w.minimized && list.push(w)))
    return list
  }, [state.columns])

  const soloWindow = useMemo(() => {
    if (!state.soloWindowId) return null
    for (const col of state.columns) {
      const win = col.windows.find((w) => w.id === state.soloWindowId && !w.minimized)
      if (win) return win
    }
    return null
  }, [state.columns, state.soloWindowId])

  return {
    columns: state.columns,
    visibleColumns,
    minimized,
    soloWindow,
    activeId: state.activeWindowId,
    soloId: state.soloWindowId,
    totalWindows,
    isFull: totalWindows >= cfg.maxWindows,
    limits: cfg,
    addWindow,
    selectEntity,
    setWindowEntity,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    focusWindow,
    setColumnSizes,
    setWindowSizes,
    setPanes,
    setSolo,
  }
}
